import {
  Box,
  ButtonGroup,
  Flex,
  IconButton,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Tooltip,
  VisuallyHidden,
} from '@chakra-ui/react';
import { ReactComponent as CropIcon } from 'assets/icons/crop.svg';
import { ReactComponent as ImageFrameIcon } from 'assets/icons/shapes/rectangle.svg';
import { AltTextMenu } from 'modules/Editor/components/Toolbar/AltTextMenu/AltTextMenu';
import { ReplaceMenuTabIndex } from 'modules/Editor/store/editorSettingsSlice.types';
import { ToolbarDivider } from 'modules/common/components/Toolbar/ToolbarDivider';
import { useTranslation } from 'react-i18next';
import { LinkMenu, ReplaceButton, useEditor, useWidget } from 'widgets/sdk';
import { InteractiveMenu } from 'widgets/sdk/Toolbar/InteractiveMenu';
import { TransformMenu } from 'widgets/sdk/TransformMenu';

import { FRAME_SHAPE_LIST, FrameShape, isSquareFrame } from './Frame';
import { CROP_BUTTON_ID } from './ImageWidget.config';
import { ImageWidgetData } from './ImageWidget.types';
import { MirrorImage } from './MirrorImage';
import AdminWidgetProps from './Toolbar/AdminWidgetProps';
import EditImageOptions from './Toolbar/EditImageOptions/EditImageOptions';
import OpacityOption from './Toolbar/OpacityOption/OpacityOption';

export const ImageWidgetToolbarMenu = ({ isSvg }: { isSvg: boolean }) => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'imageToolbar.toolbar' });
  const { widgetId, hyperlink } = useWidget<ImageWidgetData>();
  const { isCropView, toggleCropView, isGroupMember } = useEditor();
  const cropLabel = t('crop');

  return (
    <Flex gap='8px' alignItems='center'>
      <ReplaceButton initialTab={ReplaceMenuTabIndex.UPLOAD} />
      {/* disable edit image for svg since some features are not supported */}
      {!isSvg && <EditImageOptions />}
      <FrameOptionMenu />
      <Tooltip hasArrow placement='bottom' label={cropLabel} bg='black'>
        <IconButton
          id={CROP_BUTTON_ID}
          onClick={toggleCropView}
          size='sm'
          variant={'icon-btn-toolbar-option'}
          aria-label={cropLabel}
          icon={<CropIcon style={{ pointerEvents: 'none' }} />}
          isActive={isCropView}
        />
      </Tooltip>
      <OpacityOption />
      <Link />
      <ToolbarDivider />
      {isGroupMember ? <MirrorImage /> : <TransformMenu />}
      <ToolbarDivider />
      <AltTextMenu widgetId={widgetId} />
      <AdminWidgetProps />
      <ToolbarDivider />
      <InteractiveMenu isDisabled={!!hyperlink} />
    </Flex>
  );
};

const Link = () => {
  const { hyperlink, updateWidget } = useWidget<ImageWidgetData>();
  const isActive = !!hyperlink;

  const onChange = (newUrl: string | null) => {
    if (newUrl === null) return;
    if (newUrl === '') return updateWidget({ hyperlink: '' });

    // When a link is added to an image - set isDecorative to false so that it will be read by screen readers
    // Remove interactions when a link is added to an image to avoid event handling conflicts on pl page
    return updateWidget({ hyperlink: newUrl, isDecorative: false, interactions: null });
  };

  return <LinkMenu isActive={isActive} onChange={onChange} value={hyperlink} />;
};

const FrameOptionMenu = () => {
  const { t } = useTranslation('editor_widget_toolbar', {
    useSuspense: false,
    keyPrefix: 'imageToolbar.toolbar.frame',
  });
  const { updateWidget, widthPx, heightPx, crop, imageRect, frame: value } = useWidget<ImageWidgetData>();
  const { boundingBox } = useEditor();

  const openButtonLabel = t('openButtonLabel');
  const tooltipLabel = t('tooltip');
  const FrameIcon = FRAME_SHAPE_LIST.find((frameShape) => frameShape.value === value)?.icon || ImageFrameIcon;

  const onChangeFrame = (frame: FrameShape) => {
    if (isSquareFrame(frame)) {
      const isWidthLarger = widthPx >= heightPx;
      const squareSidePx = Math.min(widthPx, heightPx);

      let newCrop = { ...crop };
      // Update the crop position to the new square size
      if (isWidthLarger) newCrop.right = imageRect.widthPx - squareSidePx - crop.left;
      else newCrop.bottom = imageRect.heightPx - squareSidePx - crop.top;

      updateWidget({ widthPx: squareSidePx, heightPx: squareSidePx, crop: newCrop, frame });

      // Prevent widget bounding box to get out of sync if the widget is resized
      return setTimeout(() => boundingBox.updateRect(), 0);
    }

    return updateWidget({ frame });
  };

  return (
    <Popover placement='bottom-start' closeOnBlur={true} closeOnEsc={true} isLazy={true}>
      {({ isOpen }) => (
        <>
          <Tooltip hasArrow placement='bottom' label={tooltipLabel} bg='black'>
            <Box>
              <PopoverTrigger>
                <IconButton
                  size='sm'
                  aria-label={openButtonLabel}
                  icon={<FrameIcon />}
                  isActive={isOpen}
                  variant={'icon-btn-toolbar-option'}
                />
              </PopoverTrigger>
            </Box>
          </Tooltip>
          <PopoverContent w='170px' boxShadow='md' padding='0'>
            <VisuallyHidden>
              <PopoverHeader>{tooltipLabel}</PopoverHeader>
            </VisuallyHidden>
            <Box p={2}>
              <ButtonGroup gap='8px' variant='outline' spacing='0' display='flex' flexWrap='wrap' size='sm'>
                {FRAME_SHAPE_LIST.map((frameShape) => {
                  const { label, value: frameValue, icon: Icon } = frameShape;
                  const isActive = value === frameValue;
                  const onClick = () => onChangeFrame(frameValue);

                  return (
                    <IconButton
                      isActive={isActive}
                      onClick={onClick}
                      aria-label={t(`shapes.${label}` as keyof typeof t)}
                      variant={'icon-btn-toolbar-option'}
                      key={frameValue}
                      icon={<Icon />}
                    />
                  );
                })}
              </ButtonGroup>
            </Box>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
};
